import React, { useMemo } from 'react';
import styled from 'styled-components';
import styles from '../../../utility/styles';
import Text from '../../shared/Text';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const NoteCard = styled.div`
    display: flex;
    gap: 20px;
    padding: 20px;
    background-color: ${styles.colors.darkBackground};
    border-radius: 12px;
    @media only screen and (max-width: 768px) {
        flex-direction: column;
    }
`

const NoteImage = styled.img`
    width: 200px;
    height: auto;
    border-radius: 8px;
    object-fit: contain;
    align-self: flex-start;
    @media only screen and (max-width: 768px) {
        width: 100%;
        max-width: 300px;
    }
`

const NoteContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-family: ${styles.fonts.geist};
`

const BulletList = styled.ul`
    margin: 0;
    padding-left: 20px;
    list-style: disc;
`

const BulletItem = styled.li`
    font-size: 13px;
    line-height: 1.6;
    color: ${styles.colors.lightGray};
    margin-bottom: 4px;
`

const DescriptionText = styled.p`
    font-size: 13px;
    line-height: 1.6;
    color: ${styles.colors.lightGray};
    margin: 0;
`

/**
 * Parse a description string into paragraphs and bullet items.
 * Tesla release notes use "- " prefixed items as bullets.
 */
function parseDescription(desc) {
    if (!desc) return null
    // Split on " - " pattern that Tesla uses for bullet points
    // Also handle actual newline-prefixed bullets
    const parts = desc.split(/\s+-\s+/)
    if (parts.length <= 1) return { type: 'text', content: desc }

    // First part is the intro text, rest are bullets
    const intro = parts[0].trim()
    const bullets = parts.slice(1).map(b => b.trim()).filter(Boolean)
    return { type: 'bullets', intro, bullets }
}

function Description({ text }) {
    const parsed = useMemo(() => parseDescription(text), [text])
    if (!parsed) return null

    if (parsed.type === 'text') {
        return <DescriptionText>{parsed.content}</DescriptionText>
    }

    return (
        <>
            {parsed.intro && <DescriptionText>{parsed.intro}</DescriptionText>}
            {parsed.bullets.length > 0 && (
                <BulletList>
                    {parsed.bullets.map((item, i) => (
                        <BulletItem key={i}>{item}</BulletItem>
                    ))}
                </BulletList>
            )}
        </>
    )
}

export default function ReleaseNotes({ notes = [] }) {
    if (!notes.length) return null

    return (
        <Wrapper>
            {notes.map((note, i) => (
                <NoteCard key={`${note.title}-${i}`}>
                    {note.dark_image_url && (
                        <NoteImage
                            src={note.dark_image_url}
                            alt={note.title}
                            loading="lazy"
                        />
                    )}
                    <NoteContent>
                        <Text.Body variant='bold' style={{ fontFamily: styles.fonts.geist }}>{note.title}</Text.Body>
                        {note.subtitle && (
                            <Text.Caption color={styles.colors.blue} style={{ fontFamily: styles.fonts.geist }}>{note.subtitle}</Text.Caption>
                        )}
                        <Description text={note.description} />
                    </NoteContent>
                </NoteCard>
            ))}
        </Wrapper>
    )
}
